import { JSX, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useTranslation } from "react-i18next";
import { ThreeDotsLoader } from "../loaders";

/**
 * Props for the ComboboxInputField component
 * @interface ComboboxInputFieldProps
 * @property {boolean} [numberValue] - Whether the value should be converted to a number
 * @property {Array<{label: string, value: string}>} options - The options to display in the combobox
 * @property {string} [searchPlaceholder] - Placeholder text for the search input
 * @property {string} [emptyMessage] - Message to display when no results are found
 * @property {React.ComponentProps<typeof PopoverContent>} [popoverProps] - Props to pass to the PopoverContent component
 * @property {boolean} [loading] - Whether the options are currently loading
 * @property {React.Ref<HTMLDivElement>} [ref] - Ref for the component
 */
interface ComboboxInputFieldProps extends Omit<FormFieldComponentProps, "ref"> {
  numberValue?: boolean;
  options: { label: string; value: string | number, disabled?: boolean }[];
  searchPlaceholder?: string;
  emptyMessage?: string;
  popoverProps?: React.ComponentProps<typeof PopoverContent>;
  loading?: boolean;
  ref?: React.Ref<HTMLDivElement>;
};

/**
 * A form field component that displays a combobox (dropdown with search functionality)
 *
 * Note: If using this component inside of a dialog, you need to pass the following prop to fix nested dialog scrolling:
 * ```tsx
 * popoverProps={{
 *   onWheel: (e) => {
 *     e.stopPropagation();
 *   }
 * }}
 * ```
 *
 * @param {ComboboxInputFieldProps} props - The component props
 * @returns {JSX.Element} The ComboboxInputField component
 */
const ComboboxInputField = (props: ComboboxInputFieldProps): JSX.Element => {
  const {
    form,
    name,
    label,
    options,
    numberValue,
    disabled,
    onKeyDown,
    className,
    searchPlaceholder,
    emptyMessage,
    popoverProps,
    loading,
    ref,
  } = props;
  const { t } = useTranslation(["common"]);
  const [open, setOpen] = useState(false);

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn(className ?? "form-item")} ref={ref} onKeyDown={onKeyDown}>
          {label && <FormLabel>{label}</FormLabel>}
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <FormControl>
                <Button
                  variant="outline"
                  role="combobox"
                  disabled={disabled}
                  className={cn("w-full justify-between truncate", !field.value && "text-muted-foreground")}
                  onClick={() => setOpen((prev) => !prev)}
                >
                  {field.value
                    ? options.find((option) => option.value === field.value)?.label
                    : t("forms.selectplaceholder", { name: label })}
                  <ChevronsUpDown className="opacity-50" />
                </Button>
              </FormControl>
            </PopoverTrigger>
            <PopoverContent
              className={cn("p-0 w-[--radix-popover-trigger-width]")}
              onWheel={(e) => {
                e.stopPropagation();
              }}
              {...popoverProps}
            >
              <Command>
                <CommandInput
                  placeholder={searchPlaceholder || t("search")}
                  className="h-9"
                />
                {loading ? (
                  <div className="w-full flex justify-center p-2">
                    <ThreeDotsLoader
                      visible={true}
                      color="#94A3B8"
                      radius={20}
                      height={6}
                      width={6}
                      ariaLabel="three-dots-loading"
                    />
                  </div>
                ) : (
                  <CommandList>
                    <CommandEmpty>{emptyMessage || t("forms.noresults")}</CommandEmpty>
                    <CommandGroup>
                      {options.map((option) => (
                        <CommandItem
                          key={option.value}
                          value={option.label} // Use label for filtering/searching
                          disabled={option.disabled}
                          onSelect={() => {
                            const newValue = numberValue ? Number(option.value) : option.value;
                            form.setValue(name, newValue);
                            setOpen(false);
                          }}
                        >
                          {option.label}
                          <Check
                            className={cn(
                              "ml-auto",
                              option.value === field.value ? "opacity-100" : "opacity-0"
                            )}
                          />
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                )}
              </Command>
            </PopoverContent>
          </Popover>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export default ComboboxInputField;
